import type { Liability } from '../../generated/generatedOpenAPI';
import type { AntibodyResponse } from '../types';
import { getLiabilityBadge } from './liabilityBadge';

const serializeLiabilities = (liabilities: Liability[] | null | undefined): string => {
  if (!liabilities || liabilities.length === 0) {
    return '';
  }
  return liabilities.map((l) => `${l.residue}@${l.index}(${l.region},${l.severity})`).join('; ');
};

// Shown in VH/VL columns when that chain is not present in the sequence
const NO_CHAIN = 'N/A';

// Map a ClonotypeDefinition enum value to the human-readable CSV label
const formatClonotypeMethod = (method: string): string => {
  switch (method) {
    case 'hilary':
      return 'Hilary';
    case 'cdr3_length':
      return 'VJ+CDR3_len';
    case 'cdr3_identity':
      return 'VJ+CDR3_id';
    case 'cdr3_only':
      return 'CDR3_only';
    default:
      return method;
  }
};

/**
 * Export clonotype data to CSV file
 *
 * Columns match the target Excel spec with nt region columns interleaved after each aa region.
 *
 * Unique_ID = IS-{expUUIDWithUnderscores}-{nodeId}
 *
 * @param data - Selected clonotypes (sequences already filtered to selected only)
 * @param filename - Name of the CSV file (default: 'antibody_sequences.csv')
 * @param clonotypeSizeMap - Map of clonotype id → total barcode count across all nodes (for Clone_size)
 * @param experimentId - Experiment UUID.
 * @param clonotypeMethod - ClonotypeDefinition enum value (e.g. 'hilary', 'cdr3_length')
 */
export const exportToCSV = (
  data: AntibodyResponse[],
  filename: string = 'antibody_sequences.csv',
  clonotypeSizeMap?: Map<number, number>,
  experimentId?: string,
  clonotypeMethod?: string,
) => {
  const headers = [
    'Unique_ID',
    'Clone_ID',
    'Node_ID',
    'Clone_size',
    'Node_size',
    'Pairing',
    'Isotype',
    'clon_method',
    'VH_V_Gene',
    'VH_J_Gene',
    'VL_V_Gene',
    'VL_J_Gene',
    'CDRH3_aa',
    'CDRL3_aa',
    'AP_score',
    'VH_aa_shms',
    'VL_aa_shms',
    'Total_shms',
    'VH_cdr3_aa_len',
    'VL_cdr3_aa_len',
    'VH_UMIs',
    'VL_UMIs',
    'Liability_status',
    'Node_barcodes',
    'VH_full_aa',
    'VH_full_nt',
    'VL_full_aa',
    'VL_full_nt',
    'FWRH1_aa',
    'FWRH1_nt',
    'CDRH1_aa',
    'CDRH1_nt',
    'FWRH2_aa',
    'FWRH2_nt',
    'CDRH2_aa',
    'CDRH2_nt',
    'FWRH3_aa',
    'FWRH3_nt',
    'FWRH4_aa',
    'FWRH4_nt',
    'FWRL1_aa',
    'FWRL1_nt',
    'CDRL1_aa',
    'CDRL1_nt',
    'FWRL2_aa',
    'FWRL2_nt',
    'CDRL2_aa',
    'CDRL2_nt',
    'FWRL3_aa',
    'FWRL3_nt',
    'FWRL4_aa',
    'FWRL4_nt',
    'VH_Liabilities',
    'VH_liability_status',
    'VL_liabilities',
    'VL_liability_status',
  ];

  const expUuidFormatted = experimentId ? experimentId.replace(/-/g, '_') : 'xxxxxxxx_xxxx_xxxx_xxxx_xxxxxxxxxxxx';
  const clonMethodLabel = clonotypeMethod ? formatClonotypeMethod(clonotypeMethod) : '';

  const rows: string[][] = [];

  data.forEach((clonotype) => {
    const cloneSize = clonotypeSizeMap?.get(clonotype.id) ?? clonotype.sequences.reduce((sum, seq) => sum + seq.metadata.barcodes.length, 0);

    clonotype.sequences.forEach((seq) => {
      const { nodeId } = seq.metadata;
      const uniqueId = `IS-${expUuidFormatted}-${nodeId}`;

      // Pairing
      let pairing = '';
      if (seq.heavyChain && seq.lightChain) {
        pairing = 'VH/VL';
      } else if (seq.heavyChain) {
        pairing = seq.heavyChain.chainType;
      } else if (seq.lightChain) {
        pairing = seq.lightChain.chainType;
      }

      const cdrH3 = seq.heavyChain?.regions?.cdr3 || '';
      const cdrL3 = seq.lightChain?.regions?.cdr3 || '';

      const hDna = seq.heavyChain?.dnaRegions ?? {};
      const lDna = seq.lightChain?.dnaRegions ?? {};

      const row = [
        uniqueId,
        clonotype.id.toString(),
        String(nodeId),
        cloneSize.toString(),
        seq.metadata.barcodes.length.toString(),
        pairing,
        seq.heavyChain ? seq.heavyChain.igIsotype || '' : NO_CHAIN,
        clonMethodLabel,
        seq.heavyChain ? seq.heavyChain.vGene || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.jGene || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.vGene || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.jGene || '' : NO_CHAIN,
        seq.heavyChain ? cdrH3 : NO_CHAIN,
        seq.lightChain ? cdrL3 : NO_CHAIN,
        (seq.metadata.aps ?? 0).toFixed(2),
        seq.heavyChain ? (seq.heavyChain.shms?.length ?? 0).toString() : NO_CHAIN,
        seq.lightChain ? (seq.lightChain.shms?.length ?? 0).toString() : NO_CHAIN,
        (seq.metadata.shms ?? 0).toString(),
        seq.heavyChain ? cdrH3.length.toString() : NO_CHAIN,
        seq.lightChain ? cdrL3.length.toString() : NO_CHAIN,
        seq.heavyChain ? (seq.heavyChain.umiCounts ?? '').toString() : NO_CHAIN,
        seq.lightChain ? (seq.lightChain.umiCounts ?? '').toString() : NO_CHAIN,
        getLiabilityBadge(seq).label,
        seq.metadata.barcodes.map((bc, i) => `${bc}(${seq.metadata.originalBarcodes[i] ?? ''})`).join(', '),
        seq.heavyChain ? seq.heavyChain.sequence || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.dnaSequence || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.sequence || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.dnaSequence || '' : NO_CHAIN,
        // Heavy chain regions — aa then nt interleaved
        seq.heavyChain ? seq.heavyChain.regions?.fwr1 || '' : NO_CHAIN,
        seq.heavyChain ? hDna.fwr1 || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.regions?.cdr1 || '' : NO_CHAIN,
        seq.heavyChain ? hDna.cdr1 || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.regions?.fwr2 || '' : NO_CHAIN,
        seq.heavyChain ? hDna.fwr2 || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.regions?.cdr2 || '' : NO_CHAIN,
        seq.heavyChain ? hDna.cdr2 || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.regions?.fwr3 || '' : NO_CHAIN,
        seq.heavyChain ? hDna.fwr3 || '' : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.regions?.fwr4 || '' : NO_CHAIN,
        seq.heavyChain ? hDna.fwr4 || '' : NO_CHAIN,
        // Light chain regions — aa then nt interleaved
        seq.lightChain ? seq.lightChain.regions?.fwr1 || '' : NO_CHAIN,
        seq.lightChain ? lDna.fwr1 || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.regions?.cdr1 || '' : NO_CHAIN,
        seq.lightChain ? lDna.cdr1 || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.regions?.fwr2 || '' : NO_CHAIN,
        seq.lightChain ? lDna.fwr2 || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.regions?.cdr2 || '' : NO_CHAIN,
        seq.lightChain ? lDna.cdr2 || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.regions?.fwr3 || '' : NO_CHAIN,
        seq.lightChain ? lDna.fwr3 || '' : NO_CHAIN,
        seq.lightChain ? seq.lightChain.regions?.fwr4 || '' : NO_CHAIN,
        seq.lightChain ? lDna.fwr4 || '' : NO_CHAIN,
        seq.heavyChain ? serializeLiabilities(seq.heavyChain.liabilities) : NO_CHAIN,
        seq.heavyChain ? seq.heavyChain.liabilityStatus || '' : NO_CHAIN,
        seq.lightChain ? serializeLiabilities(seq.lightChain.liabilities) : NO_CHAIN,
        seq.lightChain ? seq.lightChain.liabilityStatus || '' : NO_CHAIN,
      ];
      rows.push(row);
    });
  });

  const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
